import React, { useState, useRef, useEffect } from "react";
import PropTypes from 'prop-types';
import {
    Badge,
    Box, Drawer, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Collapse, Typography, Fab, useTheme, useMediaQuery, IconButton
} from "@mui/material";
import {
    HomeOutlined, SettingsOutlined, ExpandLess, ExpandMore,
    ChevronLeft, ChevronRight,     StoreMallDirectoryOutlined, PeopleOutlined,
    CategoryOutlined, ProductionQuantityLimitsOutlined, BuildOutlined, LogoutOutlined,
    Menu as MenuIcon, EmailOutlined, SupportAgentOutlined, ArticleOutlined, ImageOutlined, LibraryBooksOutlined,
    Sync as SyncIcon, Storage as StorageIcon, Payment as PaymentIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { postRefreshIndex, getIndexStatus } from "../../services/catalogServices";
import { REINDEX_POLL_MS } from "../../utils/indexPollingUtils";
import { syncStripeCustomers } from "../../services/billingServices";
import { refreshCategoriesCache, refreshSelectedProductsCache } from "../../services/cacheSyncService";

import ConfirmationModal from "../Shared/confirmationModal";
import IndexProgressDialog from "./IndexProgressDialog";
import { useAuth } from "../Context/authProvider";

import Logo from "../../assets/image/logo-negativo-blu.png";
import { useAdminNotificationUnread } from "../Context/notificationContext";
import { useAdminTicketUnread } from "../Context/ticketUnreadContext";
import { useUI } from "../Context/UIContext";
import { usePermissions } from "../Context/permissionsProvider";

function buildMenuIcon(item, unreadCount, ticketBadgeCount) {
    const { path, icon } = item;
    const showUnreadBadge = path === "/dashboard" && unreadCount > 0;
    const showTicketBadge = path === "/gestione-tickets" && ticketBadgeCount > 0;
    if (!showUnreadBadge && !showTicketBadge) return icon;
    const badgeCount = showTicketBadge ? ticketBadgeCount : unreadCount;
    return (
        <Badge
            badgeContent={badgeCount > 99 ? "99+" : badgeCount}
            color="error"
            overlap="circular"
            sx={{
                "& .MuiBadge-badge": {
                    fontFamily: "inherit",
                    fontWeight: 700,
                    fontSize: 11,
                },
            }}
        >
            {icon}
        </Badge>
    );
}

function handleMenuButtonClick(item, { handleSubmenuClick, handleNavigate }) {
    const { text, subItems, onTap, path } = item;
    if (subItems) {
        handleSubmenuClick(text, subItems);
        return;
    }
    if (onTap) {
        onTap();
        return;
    }
    handleNavigate(path);
}

const AdminMenu = ({ activeRoute }) => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openSubmenus, setOpenSubmenus] = useState({});

    useEffect(() => {
        if (activeRoute?.startsWith("/gestione-contenuti")) {
            setOpenSubmenus((prev) => ({ ...prev, "Gestione Contenuti": true }));
        }
    }, [activeRoute]);
    const [modalLogoutOpen, setModalLogoutOpen] = useState(false);
    const [indexDialogOpen, setIndexDialogOpen] = useState(false);
    const [indexMinimized, setIndexMinimized] = useState(false);
    const [indexStatus, setIndexStatus] = useState(null);
    const indexPollRef = useRef(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { logout } = useAuth() || {};
    const { unreadCount } = useAdminNotificationUnread();
    const { menuBadgeCount: ticketBadgeCount } = useAdminTicketUnread();
    const { showSuccess, showError, showLoading, hideOverlay } = useUI();
    const { featureFlags } = usePermissions();

    const stopIndexPolling = () => {
        if (indexPollRef.current) {
            clearInterval(indexPollRef.current);
            indexPollRef.current = null;
        }
    };

    const pollIndexStatus = async () => {
        try {
            const status = await getIndexStatus();
            setIndexStatus(status);
            if (!status.running) {
                stopIndexPolling();
            }
        } catch {
            stopIndexPolling();
            setIndexStatus({ running: false, phase: "FAILED", percentage: 0, processed: 0, total: 0 });
        }
    };

    const startIndexPolling = () => {
        stopIndexPolling();
        pollIndexStatus();
        indexPollRef.current = setInterval(pollIndexStatus, REINDEX_POLL_MS);
    };

    const handleSyncIndex = async () => {
        setIndexStatus({ running: true, phase: "INDEXING", percentage: 0, processed: 0, total: 0 });
        setIndexMinimized(false);
        setIndexDialogOpen(true);
        try {
            await postRefreshIndex();
        } catch {
            // Potrebbe essere già in corso: ci agganciamo allo stato attuale del reindex
            const current = await getIndexStatus().catch(() => null);
            if (!current || !current.running) {
                setIndexDialogOpen(false);
                showError("Errore durante l'avvio dell'indicizzazione.");
                return;
            }
        }
        startIndexPolling();
    };

    // CHIUDI: se l'indicizzazione è ancora in corso riduce a widget, altrimenti chiude del tutto
    const handleCloseIndexDialog = () => {
        if (indexStatus?.running) {
            setIndexDialogOpen(false);
            setIndexMinimized(true);
            return;
        }
        stopIndexPolling();
        setIndexDialogOpen(false);
        setIndexMinimized(false);
    };

    // Riapre il dialog dal widget ridotto
    const handleExpandIndexDialog = () => {
        setIndexMinimized(false);
        setIndexDialogOpen(true);
    };

    // Chiude definitivamente il widget ridotto e ferma il polling
    const handleDismissIndex = () => {
        stopIndexPolling();
        setIndexMinimized(false);
        setIndexDialogOpen(false);
    };

    // Ferma il polling quando il menu viene smontato
    useEffect(() => {
        return () => {
            if (indexPollRef.current) {
                clearInterval(indexPollRef.current);
                indexPollRef.current = null;
            }
        };
    }, []);

    const handleSyncStripe = async () => {
        try {
            showLoading("Sincronizzazione in corso con Stripe...");
            const result = await syncStripeCustomers();
            hideOverlay();
            showSuccess(`Sincronizzazione completata! Vendor sincronizzati: ${result.vendorListIds?.length || 0}`);
        } catch {
            hideOverlay();
            showError("Errore durante la sincronizzazione con Stripe");
        }
    };

    const handleRefreshCategoriesCache = async () => {
        try {
            showLoading("Rigenerazione cache categorie in corso...");
            await refreshCategoriesCache();
            hideOverlay();
            showSuccess("Cache categorie rigenerata con successo.");
        } catch {
            hideOverlay();
            showError("Errore durante la rigenerazione cache categorie.");
        }
    };

    const handleRefreshSelectedProductsCache = async () => {
        try {
            showLoading("Rigenerazione cache prodotti selezionati in corso...");
            await refreshSelectedProductsCache();
            hideOverlay();
            showSuccess("Cache prodotti selezionati rigenerata con successo.");
        } catch {
            hideOverlay();
            showError("Errore durante la rigenerazione cache prodotti selezionati.");
        }
    };

    const allMenuItems = [
        {
            text: "Dashboard",
            icon: <HomeOutlined />,
            path: "/dashboard",
            hidden: !featureFlags.enableDashboard,
        },
        {
            text: "Gestione E-Commerce",
            icon: <StoreMallDirectoryOutlined />,
            path: "/gestione-ecommerce",
            hidden: !featureFlags.enableGestioneEcommerce,
        },
        {
            text: "Gestione Prodotti",
            icon: <ProductionQuantityLimitsOutlined />,
            path: "/gestione-prodotti",
            hidden: !featureFlags.enableGestioneProdotti,
        },
        {
            text: "Manutenzione",
            icon: <BuildOutlined />,
            hidden: !featureFlags.enableManutenzioneCataloghi,
            subItems: [
                {
                    text: "Cataloghi",
                    icon: <BuildOutlined />,
                    path: "/manutenzione-cataloghi",
                    hidden: !featureFlags.enableManutenzioneCataloghi,
                },
            ],
        },
        {
            text: "Gestione Categorie",
            icon: <CategoryOutlined />,
            path: "/gestione-categorie",
            hidden: !featureFlags.enableGestioneCategorie,
        },
        {
            text: "Gestione Utenti",
            icon: <PeopleOutlined />,
            path: "/gestione-utenti",
            hidden: !featureFlags.enableGestioneUtenti,
        },
        {
            text: "Gestione Tickets",
            icon: <SupportAgentOutlined />,
            path: "/gestione-tickets",
            hidden: !featureFlags.enableGestioneTickets,
        },
        {
            text: "Newsletter",
            icon: <EmailOutlined />,
            path: "/newsletter",
            hidden: !featureFlags.enableNewsletter,
        },
        {
            text: "Gestione Contenuti",
            icon: <ArticleOutlined />,
            path: "/gestione-contenuti",
            hidden: !featureFlags.enableGestioneContenuti,
            subItems: [
                {
                    text: "Gestione Banners",
                    icon: <ImageOutlined />,
                    path: "/gestione-contenuti/banner",
                    hidden: !featureFlags.enableGestioneBanners,
                },
                {
                    text: "Magazine",
                    icon: <LibraryBooksOutlined />,
                    path: "/gestione-contenuti/magazine",
                    hidden: !featureFlags.enableMagazine,
                },
            ],
        },
        {
            text: "Sincronizzazione",
            icon: <SyncIcon />,
            path: "/sincronizzazione",
            hidden: !featureFlags.enableSincronizzazione,
        },
        {
            text: "Impostazioni",
            icon: <SettingsOutlined />,
            path: "/impostazioni",
            hidden: !featureFlags.enableImpostazioni,
        },
        {
            text: "Logout",
            icon: <LogoutOutlined />,
            onTap: () => setModalLogoutOpen(true)
        }
    ];

    // Nasconde le voci con `hidden` (vedi src/config/featureConfig.js), filtra le
    // sotto-voci nascoste e rimuove la voce padre se non le resta alcuna sotto-voce.
    const visibleMenuItems = allMenuItems
        .filter((item) => !item.hidden)
        .map((item) =>
            item.subItems
                ? { ...item, subItems: item.subItems.filter((sub) => !sub.hidden) }
                : item
        )
        .filter((item) => !item.subItems || item.subItems.length > 0);

    const menuItems = visibleMenuItems;

    const toggleMenu = () => setExpanded(!expanded);

    const handleSubmenuClick = (text, subitems) => {
        setOpenSubmenus(prev => ({
            ...prev,
            [text]: !prev[text],
        }));

    };

    const handleNavigate = (path) => {
        sessionStorage.removeItem("ecommerce_filters");
        sessionStorage.removeItem("newsletter_filters");
        sessionStorage.removeItem("users_filters");
        navigate(path);
        if (isMobile) setMobileOpen(false);
    };

    const handleClickLogout = () => {
        logout?.();
        navigate("/login/admin");
    };

    const baseWidth = expanded ? 250 : 80;
    const drawerWidth = isMobile ? 250 : baseWidth;

    const renderSubmenuItem = (sub, subIndex, isSubActive) => (
        <ListItem key={sub.text || subIndex} disablePadding sx={{ width: "100%" }}>
            <ListItemButton
                onClick={() => {
                    if (sub.onTap) sub.onTap();
                    else if (sub.path) navigate(sub.path);
                }}
                sx={{
                    borderRadius: "10px",
                    my: "3px",
                    pl: 1,
                    py: isMobile ? 1 : 0.5,
                    transition: "background-color 0.2s ease",
                    "&:hover": { backgroundColor: "#2a5cad" }
                }}
            >
                <ListItemIcon sx={{ color: "white", display: "flex", justifyContent: "center" }}>
                    {sub.icon}
                </ListItemIcon>
                {expanded && (
                    <Typography
                        sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            fontSize: 14,
                            textOverflow: "ellipsis",
                            color: "white",
                            fontWeight: isSubActive ? "bold" : "normal"
                        }}
                    >
                        {sub.text}
                    </Typography>
                )}
            </ListItemButton>
        </ListItem>
    );

    const isRouteActive = (item) => {
        if (item.path === activeRoute) return true;
        return item.subItems?.some(sub => activeRoute === sub.path || activeRoute.startsWith(`${sub.path}/`));
    };

    const renderMenuButton = (item, isMacroActive) => {
        const { text, subItems } = item;
        const menuIcon = buildMenuIcon(item, unreadCount, ticketBadgeCount);
        const ExpandIcon = openSubmenus[text] ? ExpandLess : ExpandMore;

        return (
            <ListItemButton
                onClick={() => handleMenuButtonClick(item, { handleSubmenuClick, handleNavigate })}
                sx={{
                    display: "flex",
                    justifyContent: expanded ? "flex-start" : "center",
                    borderRadius: "12px",
                    py: isMobile ? 1.25 : 0.75,
                    my: isMobile ? 0.25 : 0,
                    backgroundColor: isMacroActive ? "white" : "inherit",
                    transition: "background-color 0.2s ease",
                    "&:hover": { backgroundColor: isMacroActive ? "#D0D0D0" : "#2a5cad" },
                }}
            >
                <ListItemIcon
                    sx={{
                        color: isMacroActive ? "#0b3d87" : "white",
                        display: "flex",
                        justifyContent: expanded ? "flex-start" : "center",
                        minWidth: "35px",
                        mr: expanded ? 1 : 0,
                    }}
                >
                    {menuIcon}
                </ListItemIcon>
                {expanded && (
                    <ListItemText
                        primary={<Typography>{text}</Typography>}
                        sx={{
                            color: isMacroActive ? "#0b3d87" : "white",
                            fontWeight: isMacroActive ? "bold" : "normal",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    />
                )}
                {subItems && expanded && (
                    <ExpandIcon sx={{ color: isMacroActive ? "#0b3d87" : "white" }} />
                )}
            </ListItemButton>
        );
    };

    const renderMenuItem = (item) => {
        const isMacroActive = isRouteActive(item);
        const { text, subItems } = item;

        return (
            <React.Fragment key={text}>
                <ListItem disablePadding>
                    {renderMenuButton(item, isMacroActive)}
                </ListItem>

                {subItems && (
                    <Collapse in={openSubmenus[text]} timeout="auto" unmountOnExit>
                        <Box sx={{ display: "flex" }}>
                            <Box
                                sx={{
                                    width: 3,
                                    backgroundColor: "white",
                                    marginLeft: expanded ? "10px" : "0px",
                                    my: "8px",
                                    borderRadius: 2,
                                }}
                            />
                            <List component="div" disablePadding sx={{ pl: 0, width: "100%" }}>
                                {subItems.map((sub, idx) => renderSubmenuItem(sub, idx, activeRoute === sub.path))}
                            </List>
                        </Box>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    return (
        <>
            {/* Hamburger Button per Mobile */}
            {isMobile && (
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={() => setMobileOpen(true)}
                    sx={{
                        position: "fixed",
                        top: 14,
                        left: 14,
                        zIndex: 1200,
                        width: 46,
                        height: 46,
                        borderRadius: "14px",
                        backgroundImage: "linear-gradient(145deg, #0e4a9e 0%, #0b3d87 100%)",
                        color: "white",
                        boxShadow: "0 6px 18px rgba(11, 61, 135, 0.35)",
                        transition: "transform 0.15s ease, box-shadow 0.2s ease",
                        "&:hover": { backgroundImage: "linear-gradient(145deg, #0b3d87 0%, #092d63 100%)", boxShadow: "0 8px 22px rgba(11, 61, 135, 0.45)" },
                        "&:active": { transform: "scale(0.94)" }
                    }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            {!isMobile && (
                <Box sx={{
                    position: "fixed",
                    left: expanded ? 230 : 60,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1300,
                    transition: "left 0.3s"
                }}>
                    <Fab
                        onClick={toggleMenu}
                        sx={{
                            height: "40px",
                            width: "40px",
                            backgroundColor: "white",
                            color: "white",
                            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                            "&:hover": { backgroundColor: "#D0D0D0" }
                        }}
                    >
                        {expanded ? <ChevronLeft style={{ color: "#0b3d87" }} /> : <ChevronRight style={{ color: "#0b3d87" }} />}
                    </Fab>
                </Box>
            )}

            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={isMobile ? mobileOpen : true}
                onClose={() => setMobileOpen(false)}
                anchor="left"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: drawerWidth,
                        transition: "width 0.3s",
                        boxSizing: "border-box",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#0b3d87",
                        backgroundImage: "linear-gradient(180deg, #0e4a9e 0%, #0b3d87 45%, #082d63 100%)",
                        color: "white",
                        border: "none",
                        borderTopRightRadius: isMobile ? "22px" : 0,
                        borderBottomRightRadius: isMobile ? "22px" : 0,
                        boxShadow: isMobile ? "0 20px 60px rgba(8, 45, 99, 0.5)" : "none",
                    },
                }}
            >
                {/* Logo */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    mt: isMobile ? 2 : 0
                }}>
                    <img
                        src={Logo}
                        alt="Admin Logo"
                        style={{
                            width: (isMobile || expanded) ? "130px" : "65px",
                            height: (isMobile || expanded) ? "90px" : "45px",
                            transition: "all 0.3s"
                        }}
                    />
                </Box>

                {/* Elenco menu */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        paddingBottom: "100px",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": { display: "none" }
                    }}
                >
                    <List sx={{ mx: 0.5 }}>
                        {menuItems.map(renderMenuItem)}
                    </List>
                </Box>


            </Drawer>

            {/* Modal di conferma logout */}
            <ConfirmationModal
                open={modalLogoutOpen}
                onClose={() => setModalLogoutOpen(false)}
                title="Sei sicuro di voler effettuare il logout?"
                subtitle=""
                message="Dovrai reinserire le tue credenziali per accedere come admin."
                icon={<LogoutOutlined sx={{ color: "white", fontSize: 50 }} />}
                onConfirm={() => {
                    handleClickLogout();
                    setModalLogoutOpen(false);
                }}
            />

            {/* Dialog con progress bar dell'indicizzazione */}
            <IndexProgressDialog
                open={indexDialogOpen}
                minimized={indexMinimized}
                status={indexStatus}
                onClose={handleCloseIndexDialog}
                onExpand={handleExpandIndexDialog}
                onDismiss={handleDismissIndex}
            />
        </>
    );
};

AdminMenu.propTypes = {
    activeRoute: PropTypes.string
};

export default AdminMenu;
 